import { CircularProgress, Divider, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { Fragment, ReactNode } from 'react';

export interface SourcingMenuItem {
  readonly label: string;
  readonly icon: ReactNode;
  readonly onClick: () => void;
  readonly disabled?: boolean;
  readonly hidden?: boolean;
  readonly loading?: boolean;
  readonly keepOpenOnClick?: boolean;
}

interface SourcingMenuProps {
  readonly sections: SourcingMenuItem[][];
  readonly menuPos: { top: number; left: number } | null;
  readonly onClose: () => void;
}

export function SourcingMenu({ sections, menuPos, onClose }: SourcingMenuProps) {
  const handleItemClick = (item: SourcingMenuItem) => {
    if (item.keepOpenOnClick === true) {
      item.onClick();
      return;
    }
    onClose();
    item.onClick();
  };

  return (
    <Menu
      open={Boolean(menuPos)}
      onClose={onClose}
      anchorReference='anchorPosition'
      anchorPosition={menuPos ?? undefined}
      onClick={(e) => e.stopPropagation()}
      slotProps={{ paper: { elevation: 4, sx: { width: 190, py: 1 } } }}
    >
      {sections.map((section, sectionIndex) => {
        const visibleItems = section.filter((item) => !item.hidden);
        if (visibleItems.length === 0) {
          return null;
        }
        const sectionKey = visibleItems.map((item) => item.label).join('-');
        return (
          <Fragment key={sectionKey}>
            {sectionIndex > 0 && <Divider />}
            {visibleItems.map((item) => {
              const icon = item.loading ? <CircularProgress size={18} color='inherit' /> : item.icon;
              return (
                <MenuItem
                  key={item.label}
                  disabled={item.disabled || item.loading}
                  onClick={() => handleItemClick(item)}
                  sx={{ px: '0.75rem', py: 1, gap: '0.75rem', typography: 'body2' }}
                >
                  <ListItemIcon sx={{ '&&': { minWidth: 'auto', color: 'text.secondary' } }}>
                    {icon}
                  </ListItemIcon>
                  <ListItemText>{item.label}</ListItemText>
                </MenuItem>
              );
            })}
          </Fragment>
        );
      })}
    </Menu>
  );
}
